import PortalDocument from "./PortalDocument";
import AbstractContainer from "./AbstractContainer";
import ViewerType from "./DataType";
import Exception from "../../shared/exception/Exception";
import MimeTypeFilter from "./mimetype/MimeTypeFilter";
import MimeTypes from "./mimetype/MimeTypes";
import Translate from "../../shared/translation/Translate";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import MimeType from "./mimetype/MimeType";
import HighlighterLanguage from "../dialog/parameter/HighlighterLanguage";
import portalConfiguration from "../../shared/configuration/PortalConfiguration";
import {detect, IDetectedMap} from "jschardet"

export interface PortalHighlightResult {
	relevance: number
	value: string
	language?: string
	rawValue: string
	code?: string
}

export default class TextContainer extends AbstractContainer {
	public static readonly MAX_LENGHT: number = 2 * 1024 * 1024;
	private loadingTask?: Promise<PortalHighlightResult>;

	public clean(): void {
		if (FunctionHelper.exists(this.loadingTask)) {
			this.loadingTask = undefined;
		}

		this.data = undefined;
		this.loaded = false;
	}

	private escape(value: string): string {
		let escapeValues: Map<string, string> = new Map<string, string>([
			["&", "&amp;"],
			["<", "&lt;"],
			[">", "&gt;"],
		]);

		return value.replace(/[&<>]/gm, (character: string) => {
			return FunctionHelper.applyValue(escapeValues.get(character), "");
		});
	}

	public async loadViewerData(portalDocument: PortalDocument, attachment?: Attachment): Promise<PortalHighlightResult> {
		if (!FunctionHelper.exists(this.data)) {
			throw new Exception(Translate.value("errors.files.corrupt", {ns: "message"}));
		}

		if (FunctionHelper.exists(this.loadingTask)) {
			return this.loadingTask!;
		}

		if (this.loaded) {
			return this.data;
		}

		this.loadingTask = new Promise<PortalHighlightResult>(async (resolve, reject): Promise<void> => {
			try {
				let binaryString: string = String.fromCharCode(...new Uint8Array(this.data));
				let charset: IDetectedMap = detect(binaryString);

				let viewerData: string = new TextDecoder(
					FunctionHelper.exists(charset.encoding) ? charset.encoding : "UTF-8"
				).decode(this.data);
				if (viewerData.length > TextContainer.MAX_LENGHT) {
					viewerData = viewerData.substr(0, TextContainer.MAX_LENGHT);
				}

				let mimeType: MimeType = portalDocument.getMimeType(attachment);
				let fileExtension: string = portalDocument.getDocumentFile()?.fileExtension!;

				if (FunctionHelper.exists(attachment)) {
					fileExtension = FunctionHelper.applyValue(attachment?.name?.split(".").pop(), "");
				}

				if (MimeTypeFilter.check(mimeType, [MimeTypes.XFDF, MimeTypes.XDP, MimeTypes.HTML])) {
					fileExtension = HighlighterLanguage.XML;
				}

				let highlightWorker: Worker = new Worker(
					"worker/highlight" + portalConfiguration.cachingPath + ".js"
				);
				highlightWorker.onmessage = (event: MessageEvent<any>): void => {
					resolve(event.data as PortalHighlightResult);
				};
				highlightWorker.postMessage({
					value: viewerData, language: fileExtension, cachingPath: portalConfiguration.cachingPath
				});
			} catch (exception) {
				reject(exception);
			}
		});

		try {
			this.data = await this.loadingTask;
			this.loaded = true;
		} catch (exception: any) {
			let portalException: Exception = new Exception(exception.message);
			portalException.originalException = exception;
			throw portalException;
		}

		return this.loadingTask;
	}

	public async getNumberOfPages() {
		return 1;
	}

	public getType(): ViewerType {
		return ViewerType.TEXT;
	}
}